import { useCallback, useMemo } from 'react';
import { pick } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import { getFragmentData } from '@e-socna/data-layer';
import type {
  FolderStateId,
  FolderWorkflowId,
  FragmentType,
} from '@e-socna/data-layer';

import { useFolderAccess } from '../../components/Access/hooks/useFolderAccess.hook';
import { FolderBusinessEntityState } from '../../components/FolderBusinessEntityState';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { FolderPage_GeneralInfo_Folder_Fragment } from '../../pages/Folder/GeneralInfo';

import type { RenderActionDialog } from '../FolderBusinessEntityState/types/action-dialog-render-props.type';

import { FolderArchiveInfoFields } from './ArchiveInfoFields';
import { useFindAllFolderActionPermissionsQuery } from './hooks/useFindAllFolderActionPermissionsQuery.hook';
import { useUpdateFolderStateSubmitFactory } from './hooks/useUpdateFolderStateSubmitFactory.hook';

interface Props {
  readonly enterEditionMode: () => void;
  readonly folder: FragmentType<typeof FolderPage_GeneralInfo_Folder_Fragment>;
  readonly isInEditionMode: boolean;
  readonly leaveEditionMode: () => void;
}

export const FolderStateForm = ({
  enterEditionMode,
  folder: pFolder,
  isInEditionMode,
  leaveEditionMode,
}: Readonly<Props>) => {
  const folder = getFragmentData(
    FolderPage_GeneralInfo_Folder_Fragment,
    pFolder,
  );

  const { folderStatesIdMap, folderWorkflowsIdMap } = useReferencesContext();

  const { folderState, folderWorkflow } = ensureDefined({
    folderWorkflow: folderWorkflowsIdMap[folder.workflowId as FolderWorkflowId],
    folderState: folderStatesIdMap[folder.stateId as FolderStateId],
  });

  const { currentNode } = ensureDefined(
    {
      currentNode: folderWorkflow.stateMachine.find(
        (node) => node.stateId === folderState._id,
      ),
    },
    'Current node not found',
  );

  const { data, isFetching } = useFindAllFolderActionPermissionsQuery(
    folder._id,
    isInEditionMode,
  );

  const useUpdateFolderStateSubmit = useUpdateFolderStateSubmitFactory(
    folder._id,
  );

  const currentNodeWithFolderState = useMemo(
    () => ({
      ...currentNode,
      ...pick(folderState, ['label', 'description', 'chip']),
    }),
    [currentNode, folderState],
  );

  const currentNodeActions = currentNode.actions;

  const renderActionDialog: RenderActionDialog = useCallback(
    (formikBag) => {
      const selectedAction = currentNodeActions.find(
        (action) => action._id === formikBag.values.actionId,
      );

      if (!selectedAction?.settings.needsCompleteArchiveInfo) {
        return null;
      }

      return {
        title: "Informations d'archive",
        // eslint-disable-next-line dot-notation -- index signature access required by noPropertyAccessFromIndexSignature
        isValid: !!formikBag.values['actionPayload'],
        content: <FolderArchiveInfoFields key={selectedAction._id} />,
      };
    },
    [currentNodeActions],
  );

  const { changeState } = useFolderAccess(folder._id, folder);

  return (
    <FolderBusinessEntityState
      actionPermissions={data?.findOneFolder.access.actions}
      areActionPermissionsLoading={isFetching}
      currentNode={currentNodeWithFolderState}
      enableAction
      formTitle="Mise à jour du Statut du Dossier"
      gate={changeState}
      isInEditionMode={isInEditionMode}
      onCancel={leaveEditionMode}
      onEdit={enterEditionMode}
      renderActionDialog={renderActionDialog}
      useUpdateStateSubmit={useUpdateFolderStateSubmit}
    />
  );
};
